import { makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Button, Dialog, DialogTitle, IconButton } from '@mui/material';
import { FeedbackColumnDefs, WhereUsedColumnDefs } from 'modules/constants/QuestionBankConstants';
import AddQuestion from 'modules/dashboard/AddQuestion';
import { apiPaths, get } from 'modules/platform-components/api-integration-util';
import QuestionViewer from 'modules/platform-components/question-viewer/QuestionViewer';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';
import React, { useCallback, useEffect, useState } from 'react';
import DeleteQuestionDialog from './DeleteQuestionDialog';
import { canDeleteQuestion } from './question-delete.service';

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  editButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(7),
  },
  // Sits left of Edit. Kept in the same absolutely-positioned row rather than inline with the
  // content so it does not move when the viewer swaps to the edit form.
  deleteButton: {
    position: 'absolute',
    top: theme.spacing(1),
    // Edit occupies 56px..120px from the right (right: 7 + MUI's 64px min-width), so 16 clears it
    // with an 8px gap instead of butting straight up against it.
    right: theme.spacing(16),
  },
}));

function QuestionViewModal(props) {
  const { open, setId, handleClose, QBCount, prevQuestion, nxtQuestion, questionData, selectedQuestion, isEditEnabled, selectedQuestionEditable, onQuestionSaved, onQuestionDeleted } = props;

    // Add isEditing state
    const [isEditing, setIsEditing] = useState(selectedQuestionEditable);
    // Delete is opt-in per host: a screen only gets the button by passing onQuestionDeleted, which
    // is also how it refreshes its own list afterwards. Screens that open this modal from INSIDE a
    // module or a test (qb, tests) deliberately don't - they already have a delete icon that means
    // "remove from this container", and a second one two clicks away meaning "destroy the question
    // everywhere" is a mis-click waiting to happen.
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const isDeleteEnabled = !!onQuestionDeleted && canDeleteQuestion();

  // Reset edit mode when a different question is selected so edit state from
  // Q1 doesn't bleed into Q2 if modal stays mounted across row clicks.
  useEffect(() => {
    setIsEditing(selectedQuestionEditable);
  }, [selectedQuestion, selectedQuestionEditable]);

  // Clear stale 'routeFrom' set by an earlier Dashboard navigation so the
  // QuestionViewer inside this modal doesn't render Dashboard-style swapped
  // Prev/Next labels.
  useEffect(() => {
    if (open) {
      sessionStorage.removeItem('routeFrom');
    }
  }, [open]);

  const classes = useStyles();
  let [whereUsedPopupgridOptions, setwhereUsedPopupgridOptions] = useState({});
  let [feedbackPopupgridOptions, setFeedbackPopupgridOptions] = useState({});
  // These two flags mean "the request has come back", not "show the grid" - the viewer needs them
  // to tell an in-flight fetch apart from a genuinely empty result. They start false accordingly.
  let [showWhereUsedGrid, setShowWhereUsedGrid] = useState(false);
  let [feedbackGrid, setFeedbackUsedGrid] = useState(false);
  let [whereUsedError, setWhereUsedError] = useState(null);
  let [feedbackError, setFeedbackError] = useState(null);

  const handleEdit = () => {
    setIsEditing(true);
  };

  // Builds a fresh options object every time. The previous version mutated the object already in
  // state and handed the same reference back to the setter, so React bailed out of the update and
  // the tab only repainted as a side effect of the showWhereUsedGrid toggle.
  const plotWhereUsedGrid = useCallback((rowData) => {
    setwhereUsedPopupgridOptions({
      pagination: true,
      columnDefs: WhereUsedColumnDefs,
      rowData: rowData || [],
    });
    setShowWhereUsedGrid(true);
  }, []);

  const plotFeedbackGrid = useCallback((rowData) => {
    setFeedbackPopupgridOptions({
      pagination: true,
      columnDefs: FeedbackColumnDefs,
      rowData: rowData || [],
    });
    setFeedbackUsedGrid(true);
  }, []);

  const loadQuestionUsage = useCallback(() => {
    if (!selectedQuestion || +selectedQuestion === 0) {
      return;
    }
    setShowWhereUsedGrid(false);
    setFeedbackUsedGrid(false);
    setWhereUsedError(null);
    setFeedbackError(null);

    get(`${apiPaths.getQuestionHistory}/${selectedQuestion}`, {
      tenantId: getUserDetails().tenantId,
    })
      .then((res) => {
        plotWhereUsedGrid(res?.result);
      })
      .catch(() => {
        // Don't fall through to plot([]) - an empty grid would claim the question is unused when
        // we simply failed to find out.
        setWhereUsedError('Could not load where this question is used. Please retry.');
        setShowWhereUsedGrid(true);
      });

    get(`${apiPaths.getQuestionFeedbackById}?libTypeList=TEST&libTypeList=QBANK&questionId=${selectedQuestion}`, {
      tenantId: getUserDetails().tenantId,
    })
      .then((res) => {
        plotFeedbackGrid(res?.result);
      })
      .catch(() => {
        setFeedbackError('Could not load student feedback. Please retry.');
        setFeedbackUsedGrid(true);
      });
  }, [plotWhereUsedGrid, plotFeedbackGrid, selectedQuestion]);

  useEffect(() => {
    loadQuestionUsage();
  }, [loadQuestionUsage]);

  const renderDialogContent = () => {
    if (isEditing) {
      return (
        <div style={{ padding: '15px' }}>
          <AddQuestion
            QBEdit={true}
            qbId={selectedQuestion}
            onExitEdit={() => setIsEditing(false)}
            // The viewer below renders from `questionData`, which the parent fetched
            // before this edit began. Ask the parent to refetch once a save lands so
            // exiting edit mode shows the saved content, not the pre-edit copy.
            onSaved={onQuestionSaved}
          />
        </div>
      );
    } else {
      return (
        <QuestionViewer
          whereUsedPopupgridOptions={whereUsedPopupgridOptions}
          feedbackPopupgridOptions={feedbackPopupgridOptions}
          prevQuestion={prevQuestion}
          nxtQuestion={nxtQuestion}
          {...questionData}
          count={QBCount}
          showWhereUsedGrid={showWhereUsedGrid}
          whereUsedError={whereUsedError}
          feedbackGrid={feedbackGrid}
          feedbackError={feedbackError}
          setId={setId}
        />
      );
    }
  };

  return (
    <div>
      {/*
        disableEnforceFocus is required because this dialog hosts Jodit editors.
        Jodit renders its toolbar popups (image URL, video URL, link) into
        document.body - see getContainer() in jodit/core/global.ts - so they land
        OUTSIDE this dialog's DOM subtree. MUI's FocusTrap listens for `focusin`
        on the document and calls rootElement.focus() whenever the active element
        is not contained by the modal root, which yanked focus straight back out
        of those popups: the URL field could be clicked but never typed into.
      */}
      <Dialog maxWidth="xl" fullWidth maxHeight="lg" open={open} onClose={handleClose} disableEnforceFocus>
        <DialogTitle>
          <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          {isEditEnabled && !isEditing && (
            <Button aria-label="edit" variant="outlined" className={classes.editButton} onClick={handleEdit}>
              Edit
            </Button>
          )}
          {/* Hidden while editing: the form has unsaved state, and deleting the question out from
              under it would leave the editor pointed at a row that no longer exists. */}
          {isDeleteEnabled && !isEditing && (
            <Button
              aria-label="delete"
              variant="outlined"
              color="error"
              className={classes.deleteButton}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Delete
            </Button>
          )}
        </DialogTitle>
        {renderDialogContent()}
      </Dialog>

      <DeleteQuestionDialog
        open={confirmDeleteOpen}
        questionIds={selectedQuestion ? [Number(selectedQuestion)] : []}
        onClose={() => setConfirmDeleteOpen(false)}
        onDeleted={() => {
          // Close the viewer first: the question it renders is gone, and leaving it up would show
          // stale content over a list that has already dropped the row.
          handleClose();
          onQuestionDeleted();
        }}
      />
    </div>
  );
}

export default QuestionViewModal;
